/**
 * PdfExportButton — Generates a printable summary of a template
 * and triggers the browser's Print/Save-as-PDF dialog.
 *
 * For branding/store: exports template metadata + content completion stats.
 * For facelift: exports project overview + all pages with statuses + annotations + screenshots.
 */

import { useState, useCallback } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { getTemplateMeta, type TemplateMeta } from "../../utils/templateApi";
import {
  getFaceliftProject, getFaceliftPages,
  getDefaultProjectData,
  CHANGE_TYPE_CONFIG, STATUS_CONFIG, PRIORITY_CONFIG, PAGE_TYPE_LABELS,
  type FaceliftProjectData, type FaceliftPageItem,
} from "../../utils/faceliftApi";
import {
  getScreenshots,
  type ScreenshotEntry,
} from "../../utils/screenshotAnnotationApi";

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* ================================================================
   Hebrew labels
   ================================================================ */

const TYPE_LABELS: Record<string, string> = {
  branding: "\u05D1\u05E8\u05E0\u05D3\u05D9\u05E0\u05D2",
  store: "\u05D7\u05E0\u05D5\u05EA",
  facelift: "\u05DE\u05EA\u05D9\u05D7\u05EA \u05E4\u05E0\u05D9\u05DD",
};

const STATUS_LABELS: Record<string, string> = {
  onboarding: "\u05D0\u05D5\u05E0\u05D1\u05D5\u05E8\u05D3\u05D9\u05E0\u05D2",
  characterization_call: "\u05E9\u05D9\u05D7\u05EA \u05D0\u05E4\u05D9\u05D5\u05DF",
  materials: "\u05D7\u05D5\u05DE\u05E8\u05D9\u05DD",
  characterization: "\u05D0\u05E4\u05D9\u05D5\u05DF",
  seo_check: "\u05D1\u05D3\u05D9\u05E7\u05EA SEO",
  client_approval: "\u05D0\u05D9\u05E9\u05D5\u05E8 \u05DC\u05E7\u05D5\u05D7",
  design: "\u05E2\u05D9\u05E6\u05D5\u05D1",
  development: "\u05E4\u05D9\u05EA\u05D5\u05D7",
  site_tree: "\u05E2\u05E5 \u05D0\u05EA\u05E8",
  matal_handling: "\u05D1\u05D8\u05D9\u05E4\u05D5\u05DC \u05DE\u05EA\u05DC",
  none: "\u05D0\u05D9\u05DF",
  monday_add: "\u05D4\u05D5\u05E1\u05E4\u05D4 \u05DC\u05DE\u05D0\u05E0\u05D3\u05D9\u05D9",
  logo: "\u05DC\u05D5\u05D2\u05D5",
  content_writing: "\u05DB\u05EA\u05D9\u05D1\u05EA \u05EA\u05D5\u05DB\u05DF",
  integrations: "\u05D7\u05D9\u05D1\u05D5\u05E8 \u05DE\u05DE\u05E9\u05E7\u05D9\u05DD",
  pm_handling: "\u05D1\u05D8\u05D9\u05E4\u05D5\u05DC \u05DE\u05E0\u05D4\u05DC \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
};

function formatDateFull(ts: number): string {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/* ================================================================
   Screenshot annotation data types for PDF
   ================================================================ */

interface PageScreenshots {
  pageId: string;
  pageName: string;
  screenshots: ScreenshotEntry[];
}

/* ================================================================
   Build the printable HTML — Facelift (enhanced with annotations)
   ================================================================ */

function buildFaceliftHtml(
  meta: TemplateMeta,
  project: FaceliftProjectData,
  pages: FaceliftPageItem[],
  pageScreenshots: PageScreenshots[],
): string {
  const rows = pages.map((p) => {
    const ct = CHANGE_TYPE_CONFIG[p.changeType];
    const st = STATUS_CONFIG[p.status];
    const pt = PAGE_TYPE_LABELS[p.pageType] || p.pageType;
    return `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${p.name}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${pt}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${p.currentUrl || "-"}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;"><span style="background:${ct.bg};color:${ct.text};padding:2px 8px;border-radius:10px;font-size:11px;">${ct.icon} ${ct.label}</span></td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;"><span style="background:${st.bg};color:${st.text};padding:2px 8px;border-radius:10px;font-size:11px;">${st.label}</span></td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${p.priority ? PRIORITY_CONFIG[p.priority].label : "-"}</td>
      </tr>`;
  }).join("");

  const infoRows = [
    ["\u05E9\u05DD \u05D4\u05D0\u05EA\u05E8", project.siteName],
    ["URL \u05E0\u05D5\u05DB\u05D7\u05D9", project.currentUrl],
    ["\u05E4\u05DC\u05D8\u05E4\u05D5\u05E8\u05DE\u05D4", project.platform],
    ["\u05D0\u05D7\u05E1\u05D5\u05DF", project.hosting],
    ["\u05D4\u05D9\u05E7\u05E3", project.projectScope === "both" ? "\u05E2\u05D9\u05E6\u05D5\u05D1 + \u05EA\u05D5\u05DB\u05DF" : project.projectScope === "design" ? "\u05E2\u05D9\u05E6\u05D5\u05D1" : "\u05EA\u05D5\u05DB\u05DF"],
    ["\u05DE\u05D8\u05E8\u05EA \u05D4\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8", project.projectGoal],
    ["\u05EA\u05D9\u05D0\u05D5\u05E8 \u05D4\u05E2\u05E1\u05E7", project.businessDescription],
    ["\u05E7\u05D4\u05DC \u05D9\u05E2\u05D3", project.targetAudience],
    ["\u05DE\u05EA\u05D7\u05E8\u05D9\u05DD", project.competitors],
    ["\u05D8\u05D5\u05DF \u05D3\u05D9\u05D1\u05D5\u05E8", project.toneOfVoice],
  ].filter(([, v]) => v).map(([label, value]) =>
    `<tr><td style="padding:4px 8px;font-weight:600;color:#374151;white-space:nowrap;">${label}</td><td style="padding:4px 8px;color:#4b5563;">${value}</td></tr>`
  ).join("");

  /* Design direction info */
  const designRows = [
    ["\u05DE\u05D4 \u05DC\u05E9\u05DE\u05D5\u05E8 \u05DE\u05D4\u05DE\u05D5\u05EA\u05D2", project.keepFromBrand],
    ["\u05E9\u05D9\u05E0\u05D5\u05D9\u05D9 \u05E2\u05D9\u05E6\u05D5\u05D1", project.designChanges],
    ["\u05D4\u05E4\u05E0\u05D9\u05D5\u05EA \u05DC\u05D4\u05E9\u05E8\u05D0\u05D4", project.referenceLinks],
    ["\u05D4\u05E2\u05E8\u05D5\u05EA \u05E2\u05D9\u05E6\u05D5\u05D1", project.designNotes],
  ].filter(([, v]) => v).map(([label, value]) =>
    `<tr><td style="padding:4px 8px;font-weight:600;color:#374151;white-space:nowrap;">${label}</td><td style="padding:4px 8px;color:#4b5563;">${value}</td></tr>`
  ).join("");

  /* Page-level notes */
  const pageNotes = pages.filter((p) => p.designNotes || p.contentNotes || p.generalNotes).map((p) => {
    const notes = [];
    if (p.designNotes) notes.push(`<b>\u05E2\u05D9\u05E6\u05D5\u05D1:</b> ${p.designNotes}`);
    if (p.contentNotes) notes.push(`<b>\u05EA\u05D5\u05DB\u05DF:</b> ${p.contentNotes}`);
    if (p.generalNotes) notes.push(`<b>\u05DB\u05DC\u05DC\u05D9:</b> ${p.generalNotes}`);
    return `
      <div style="margin-bottom:12px;border-right:3px solid #3a6bc5;padding-right:12px;">
        <h4 style="font-size:13px;color:#1a4494;margin:0 0 4px;">${p.name}</h4>
        <div style="font-size:12px;color:#4b5563;line-height:1.6;">${notes.join("<br/>")}</div>
      </div>`;
  }).join("");

  /* Screenshot annotations */
  let screenshotSection = "";
  const pagesWithScreenshots = pageScreenshots.filter((ps) => ps.screenshots.length > 0);
  if (pagesWithScreenshots.length > 0) {
    const screenshotItems = pagesWithScreenshots.map((ps) => {
      const items = ps.screenshots.map((ss) => {
        const annotationList = ss.annotations.length > 0
          ? ss.annotations.map((a, i) =>
            `<div style="margin:4px 0;font-size:11px;color:#4b5563;">
              <span style="display:inline-block;width:18px;height:18px;border-radius:50%;background:${a.color};color:#fff;text-align:center;line-height:18px;font-size:10px;font-weight:bold;margin-left:6px;">${i + 1}</span>
              ${a.text} <span style="color:#9ca3af;">(\u05E2\u05DC \u05D9\u05D3\u05D9 ${a.author})</span>
            </div>`
          ).join("")
          : `<div style="font-size:11px;color:#9ca3af;">\u05D0\u05D9\u05DF \u05D4\u05E2\u05E8\u05D5\u05EA</div>`;

        return `
          <div style="margin-bottom:16px;">
            <div style="font-size:12px;font-weight:600;color:#374151;margin-bottom:4px;">${ss.label || "\u05E6\u05D9\u05DC\u05D5\u05DD \u05DE\u05E1\u05DA"}</div>
            <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px;">
              ${annotationList}
            </div>
          </div>`;
      }).join("");
      return `
        <div style="margin-bottom:20px;">
          <h4 style="font-size:13px;color:#1a4494;margin:0 0 8px;border-right:3px solid #3a6bc5;padding-right:8px;">${ps.pageName}</h4>
          ${items}
        </div>`;
    }).join("");

    screenshotSection = `
      <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;page-break-before:auto;">\u05E6\u05D9\u05DC\u05D5\u05DE\u05D9 \u05DE\u05E1\u05DA \u05D5\u05D4\u05E2\u05E8\u05D5\u05EA</h3>
      ${screenshotItems}`;
  }

  /* Contact info */
  const contactRows = [
    ["\u05D8\u05DC\u05E4\u05D5\u05DF SEO", project.contactPhoneSeo],
    ["\u05D8\u05DC\u05E4\u05D5\u05DF \u05D9\u05E9\u05D9\u05E8", project.contactPhoneDirect],
    ["\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA", project.contactHours],
    ["\u05DB\u05EA\u05D5\u05D1\u05EA", project.contactAddress],
    ["\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC", project.contactEmail],
  ].filter(([, v]) => v).map(([label, value]) =>
    `<tr><td style="padding:3px 8px;font-weight:600;color:#374151;font-size:12px;white-space:nowrap;">${label}</td><td style="padding:3px 8px;color:#4b5563;font-size:12px;">${value}</td></tr>`
  ).join("");

  return `
    <div style="max-width:900px;margin:0 auto;font-family:Arial,sans-serif;direction:rtl;padding:40px 20px;">
      <div style="text-align:center;margin-bottom:30px;">
        <h1 style="font-size:24px;color:#1a4494;margin:0;">\u05D1\u05E8\u05D9\u05E3 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8 \u05DE\u05EA\u05D9\u05D7\u05EA \u05E4\u05E0\u05D9\u05DD</h1>
        <h2 style="font-size:18px;color:#374151;margin:8px 0 4px;">${meta.clientName}</h2>
        <p style="font-size:12px;color:#9ca3af;">\u05EA\u05D0\u05E8\u05D9\u05DA \u05D9\u05D9\u05E6\u05D5\u05D0: ${formatDateFull(Date.now())} | \u05D3\u05D3\u05DC\u05D9\u05D9\u05DF: ${meta.deadline ? formatDateFull(meta.deadline) : "\u05DC\u05D0 \u05E0\u05E7\u05D1\u05E2"}</p>
      </div>

      <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;">\u05E4\u05E8\u05D8\u05D9 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px;">${infoRows}</table>

      ${designRows ? `
        <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;">\u05DB\u05D9\u05D5\u05D5\u05DF \u05E2\u05D9\u05E6\u05D5\u05D1\u05D9</h3>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px;">${designRows}</table>
      ` : ""}

      ${contactRows ? `
        <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;">\u05E4\u05E8\u05D8\u05D9 \u05E7\u05E9\u05E8</h3>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px;">${contactRows}</table>
      ` : ""}

      <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;">\u05E8\u05E9\u05D9\u05DE\u05EA \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD (${pages.length})</h3>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f3f4f6;">
            <th style="padding:8px;text-align:right;">\u05E9\u05DD</th>
            <th style="padding:8px;text-align:right;">\u05E1\u05D5\u05D2</th>
            <th style="padding:8px;text-align:right;">URL</th>
            <th style="padding:8px;text-align:right;">\u05E1\u05D5\u05D2 \u05E9\u05D9\u05E0\u05D5\u05D9</th>
            <th style="padding:8px;text-align:right;">\u05E1\u05D8\u05D8\u05D5\u05E1</th>
            <th style="padding:8px;text-align:right;">\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      ${pageNotes ? `
        <h3 style="font-size:16px;color:#1a4494;border-bottom:2px solid #e8edf8;padding-bottom:6px;margin-top:30px;">\u05D4\u05E2\u05E8\u05D5\u05EA \u05DC\u05E4\u05D9 \u05E2\u05DE\u05D5\u05D3</h3>
        ${pageNotes}
      ` : ""}

      ${screenshotSection}

      <div style="margin-top:40px;text-align:center;color:#9ca3af;font-size:11px;border-top:1px solid #e5e7eb;padding-top:20px;">
        <p>ZAP WEB \u2014 \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4</p>
      </div>
    </div>
  `;
}

function buildGenericHtml(meta: TemplateMeta): string {
  const infoRows = [
    ["\u05E9\u05DD \u05DC\u05E7\u05D5\u05D7", meta.clientName],
    ["\u05DE\u05E1\u05E4\u05E8 \u05DC\u05E7\u05D5\u05D7", meta.clientNumber || "-"],
    ["\u05E1\u05D5\u05D2", TYPE_LABELS[meta.templateType] || meta.templateType],
    ["\u05E9\u05DC\u05D1 \u05D1\u05E4\u05DC\u05D5\u05D0\u05D5", STATUS_LABELS[meta.status || "none"] || meta.status || "\u05D0\u05D9\u05DF"],
    ["\u05D0\u05D7\u05E8\u05D9\u05D5\u05EA \u05E0\u05D5\u05DB\u05D7\u05D9\u05EA", (meta as any).currentOwner || "-"],
    ["\u05DE\u05D0\u05E4\u05D9\u05D9\u05E0\u05EA", meta.characterizer || "-"],
    ["\u05DE\u05E2\u05E6\u05D1\u05EA", meta.designer || "-"],
    ["\u05DB\u05D5\u05EA\u05D1\u05EA \u05EA\u05D5\u05DB\u05DF", meta.contentWriter || "-"],
    ["\u05DE\u05E4\u05EA\u05D7", meta.developer || "-"],
    ["\u05DE\u05E7\u05D3\u05DE\u05EA", meta.marketer || "-"],
    ["\u05D3\u05D3\u05DC\u05D9\u05D9\u05DF", meta.deadline ? formatDateFull(meta.deadline) : "\u05DC\u05D0 \u05E0\u05E7\u05D1\u05E2"],
    ["\u05EA\u05D0\u05E8\u05D9\u05DA \u05D9\u05E6\u05D9\u05E8\u05D4", formatDateFull(meta.createdAt)],
    ["\u05E2\u05D3\u05DB\u05D5\u05DF \u05D0\u05D7\u05E8\u05D5\u05DF", formatDateFull(meta.updatedAt)],
    ["\u05EA\u05D2\u05D9\u05D5\u05EA", (meta.tags || []).join(", ") || "-"],
  ].map(([label, value]) =>
    `<tr><td style="padding:6px 10px;font-weight:600;color:#374151;width:140px;">${label}</td><td style="padding:6px 10px;color:#4b5563;">${value}</td></tr>`
  ).join("");

  return `
    <div style="max-width:700px;margin:0 auto;font-family:Arial,sans-serif;direction:rtl;padding:40px 20px;">
      <div style="text-align:center;margin-bottom:30px;">
        <h1 style="font-size:24px;color:#1a4494;margin:0;">\u05E1\u05D9\u05DB\u05D5\u05DD \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA</h1>
        <h2 style="font-size:20px;color:#374151;margin:8px 0 4px;">${meta.clientName}</h2>
        <p style="font-size:12px;color:#9ca3af;">\u05EA\u05D0\u05E8\u05D9\u05DA \u05D9\u05D9\u05E6\u05D5\u05D0: ${formatDateFull(Date.now())}</p>
      </div>

      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <tbody>${infoRows}</tbody>
      </table>

      <div style="margin-top:40px;text-align:center;color:#9ca3af;font-size:11px;">
        <p>ZAP WEB \u2014 \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4</p>
      </div>
    </div>
  `;
}

/* ================================================================
   Component
   ================================================================ */

interface Props {
  templateId: string;
  className?: string;
}

export function PdfExportButton({ templateId, className = "" }: Props) {
  const [loading, setLoading] = useState(false);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      const meta = await getTemplateMeta(templateId);
      if (!meta) {
        alert("\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05E0\u05EA\u05D5\u05E0\u05D9 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
        return;
      }

      let html: string;

      if (meta.templateType === "facelift") {
        const [project, pages] = await Promise.all([
          getFaceliftProject(templateId),
          getFaceliftPages(templateId),
        ]);

        // Fetch screenshots for each page
        const pageScreenshots: PageScreenshots[] = [];
        try {
          const screenshotPromises = (pages || []).map(async (page) => {
            const screenshots = await getScreenshots(templateId, page.id).catch(() => []);
            return { pageId: page.id, pageName: page.name, screenshots };
          });
          const results = await Promise.all(screenshotPromises);
          pageScreenshots.push(...results);
        } catch {
          // Continue without screenshots
        }

        html = buildFaceliftHtml(meta, project || getDefaultProjectData(), pages || [], pageScreenshots);
      } else {
        html = buildGenericHtml(meta);
      }

      // Open print window
      const win = window.open("", "_blank", "width=900,height=700");
      if (!win) {
        alert("\u05D7\u05DC\u05D5\u05DF \u05D4\u05E7\u05D5\u05E4\u05E5 \u05E0\u05D7\u05E1\u05DD \u2014 \u05D0\u05E0\u05D0 \u05D4\u05EA\u05D9\u05E8\u05D5 \u05D7\u05DC\u05D5\u05EA \u05E7\u05D5\u05E4\u05E6\u05D9\u05DD");
        return;
      }
      win.document.open();
      win.document.write(`<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8">
  <title>${meta.clientName} \u2014 \u05D1\u05E8\u05D9\u05E3</title>
  <style>
    @media print {
      body { margin: 0; }
      @page { margin: 1cm; }
    }
    body { margin: 0; padding: 0; background: #fff; }
  </style>
</head>
<body>${html}</body>
</html>`);
      win.document.close();

      // Trigger print after content loads
      setTimeout(() => {
        win.print();
      }, 300);
    } catch (err) {
      console.error("[PdfExport] Error:", err);
      alert("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05D9\u05E6\u05D5\u05D0 \u05D4-PDF");
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  return (
    <button
      type="button"
      className={`${btnReset} flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-800 transition-transform ${className}`}
      onClick={handleExport}
      disabled={loading}
      title={"\u05D9\u05D9\u05E6\u05D5\u05D0 \u05D1\u05E8\u05D9\u05E3 PDF"}
      aria-label={"\u05D9\u05D9\u05E6\u05D5\u05D0 \u05D1\u05E8\u05D9\u05E3 PDF"}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
      <span>{"\u05D1\u05E8\u05D9\u05E3 PDF"}</span>
    </button>
  );
}